import styles from 'css/src/components/abw-franchise/styles.module.scss';

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Plan } from 'js/src/client';
import { ApiService } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { BUTTON_VARIANTS } from 'js/src/components/common/ui/Button';
import BillingPeriodToggle from 'js/src/components/pricing/BillingPeriodToggle';
import NotificationBlock from 'js/src/components/pricing/NotificationBlock';
import PlanSwitchErrorHandlerModal from 'js/src/components/pricing/PlanSwitchErrorHandlerModal';
import SubscriptionChangeModal from 'js/src/components/pricing/SubscriptionChangeModal';
import type { PlanSwitchErrorInfo } from 'js/src/components/pricing/types';
import { UPGRADE_STATUS } from 'js/src/components/pricing/types';
import UpgradeButton from 'js/src/components/pricing/UpgradeButton';
import {
    handlePlanSwitchError as handlePlanSwitchErrorThunk,
    loadNewCheckoutSession,
    prepareSubscriptionChange,
    togglePlanSwitchErrorModal as togglePlanSwitchErrorModalThunk,
    updateSubscription as updateSubscriptionThunk,
    waitForPlanToUpdate,
} from 'js/src/data/pricing/pricing-operations';
import {
    setAnnualPlans,
    setChangingSubscription,
    setConfirmationModalData,
    setCouponStatus,
    setMonthlyPlans,
    setSelectedMonthly,
    setShowSubscriptionChangeConfirmation,
    setSubscriptionDetails,
    setUpgradeStatus,
    setUserHasPaymentMethodSetup,
} from 'js/src/data/pricing/pricing-slice';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { getQueryParam } from 'js/src/libs/querystring';
import type { RootState } from 'js/src/libs/RootState';
import { getPlanDisplayName } from 'js/src/libs/server-utils';

const ABW_PLAN_NAMES = ['ABW_Franchise', 'ABW_Franchise_Annual'];

const ABWFranchisePage: React.FC = () => {
    const dispatch = useDispatch();

    const selectedMonthly = useSelector((state: RootState) => state.pricing.selectedMonthly);
    const monthlyPlans = useSelector((state: RootState) => state.pricing.monthlyPlans);
    const annualPlans = useSelector((state: RootState) => state.pricing.annualPlans);
    const couponStatus = useSelector((state: RootState) => state.pricing.couponStatus);
    const showSubscriptionChangeConfirmation = useSelector(
        (state: RootState) => state.pricing.showSubscriptionChangeConfirmation,
    );
    const changingSubscription = useSelector(
        (state: RootState) => state.pricing.changingSubscription,
    );
    const upgradeStatus = useSelector((state: RootState) => state.pricing.upgradeStatus);
    const subscriptionDetails = useSelector(
        (state: RootState) => state.pricing.subscriptionDetails,
    );
    const confirmationModalData = useSelector(
        (state: RootState) => state.pricing.confirmationModalData,
    );
    const showPlanSwitchErrorModal = useSelector(
        (state: RootState) => state.pricing.showPlanSwitchErrorModal,
    );
    const planSwitchErrorInfo = useSelector(
        (state: RootState) => state.pricing.planSwitchErrorInfo,
    );
    const userHasPaymentMethodSetup = useSelector(
        (state: RootState) => state.pricing.userHasPaymentMethodSetup,
    );

    const loadPlans = React.useCallback(() => {
        ApiService.pricingPlansRetrieve({ query: { coupon: getQueryParam('cpn')! } })
            .then((response) => {
                dispatch(setMonthlyPlans(response.data.monthly_plans));
                dispatch(setAnnualPlans(response.data.annual_plans));
                dispatch(setCouponStatus(response.data.coupon_status));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading pricing plans. Please refresh page.');
            });
    }, [dispatch]);

    const loadSubscriptionDetails = React.useCallback(() => {
        if (!SERVER.USER) {
            return;
        }
        ApiService.pricingSubscriptionRetrieve()
            .then((response) => {
                dispatch(setSubscriptionDetails(response.data));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading subscription details. Please refresh page.');
            });
    }, [dispatch]);

    const loadPaymentMethodStatus = React.useCallback(async () => {
        if (!SERVER.USER) {
            return;
        }
        try {
            const response = await ApiService.pricingPaymentMethodStatusRetrieve();
            dispatch(setUserHasPaymentMethodSetup(response.data.has_payment_configured));
        } catch (error: unknown) {
            ErrorReporter.capture(error);
            Toast.error('Error loading payment method status. Please refresh page.');
        }
    }, [dispatch]);

    React.useEffect(() => {
        loadPlans();
        loadSubscriptionDetails();
        loadPaymentMethodStatus();
    }, [loadPlans, loadSubscriptionDetails, loadPaymentMethodStatus]);

    React.useEffect(() => {
        let upgradeStatusInitial = null;
        let changingSubscriptionInitial = false;
        if (SERVER.USER) {
            if (getQueryParam('success') === 'true') {
                upgradeStatusInitial = UPGRADE_STATUS.SUCCESS;
            } else if (getQueryParam('pm_updated') === 'true') {
                const selected_plan = getQueryParam('selected_plan');
                if (SERVER.USER.plan === selected_plan) {
                    upgradeStatusInitial = UPGRADE_STATUS.SUCCESS;
                } else {
                    changingSubscriptionInitial = true;
                }
            }
        }

        dispatch(setChangingSubscription(changingSubscriptionInitial));
        dispatch(setUpgradeStatus(upgradeStatusInitial));

        if (getQueryParam('pm_updated') === 'true' && changingSubscriptionInitial) {
            const selected_plan = getQueryParam('selected_plan');
            dispatch(waitForPlanToUpdate(selected_plan!));
        }
    }, [dispatch]);

    const isCouponValid = couponStatus?.valid;

    React.useEffect(() => {
        if (isCouponValid) {
            dispatch(setSelectedMonthly(true));
        } else if (subscriptionDetails?.is_monthly !== undefined && subscriptionDetails.price > 0) {
            dispatch(setSelectedMonthly(subscriptionDetails.is_monthly));
        }
    }, [isCouponValid, subscriptionDetails?.is_monthly, subscriptionDetails?.price, dispatch]);

    const selectMonthly = React.useCallback(() => {
        dispatch(setSelectedMonthly(true));
    }, [dispatch]);

    const selectYearly = React.useCallback(() => {
        dispatch(setSelectedMonthly(false));
    }, [dispatch]);

    const switchToPlan = React.useCallback(
        async (plan: Plan) => {
            // If user has payment method setup, show confirmation modal and use PATCH endpoint
            if (userHasPaymentMethodSetup && subscriptionDetails?.status !== 'incomplete') {
                dispatch(
                    setConfirmationModalData({
                        plan,
                        email: SERVER.USER.email,
                    }),
                );
                dispatch(setShowSubscriptionChangeConfirmation(true));
                return;
            }

            // Otherwise create checkout session (POST endpoint)
            dispatch(prepareSubscriptionChange());

            let coupon = '';
            if (couponStatus !== null && couponStatus.valid) {
                coupon = couponStatus.id;
            }

            try {
                const response = await ApiService.pricingSubscriptionCreate({
                    body: {
                        plan_name: plan.name,
                        email: SERVER.USER.email,
                        coupon: coupon,
                        success_path: '/abw-mortgage/',
                        cancel_path: '/abw-mortgage/',
                    },
                });
                const checkout_session_id = response.data.checkout_session_id;
                dispatch(loadNewCheckoutSession(checkout_session_id));
            } catch (error: unknown) {
                ErrorReporter.capture(error);
                Toast.error('Error creating checkout session. Please try again.');
                dispatch(setChangingSubscription(false));
            }
        },
        [dispatch, couponStatus, userHasPaymentMethodSetup, subscriptionDetails],
    );

    const updateSubscription = React.useCallback(
        (email: string, plan: Plan) => {
            dispatch(updateSubscriptionThunk(email, plan));
        },
        [dispatch],
    );

    const closeSubscriptionChangeModal = React.useCallback(() => {
        dispatch(setShowSubscriptionChangeConfirmation(false));
    }, [dispatch]);

    const handlePlanSwitchError = React.useCallback(
        (errorInfo: PlanSwitchErrorInfo) => {
            dispatch(handlePlanSwitchErrorThunk(errorInfo));
        },
        [dispatch],
    );

    const togglePlanSwitchErrorModal = React.useCallback(
        (visibilityFlag: boolean) => {
            dispatch(togglePlanSwitchErrorModalThunk(visibilityFlag));
        },
        [dispatch],
    );

    const currentPlan = React.useMemo(() => {
        const plans = selectedMonthly ? monthlyPlans : annualPlans;
        return plans.find((plan) => ABW_PLAN_NAMES.includes(plan.name));
    }, [selectedMonthly, monthlyPlans, annualPlans]);

    const retryUpgrade = React.useCallback(() => {
        if (currentPlan) {
            switchToPlan(currentPlan);
        }
    }, [currentPlan, switchToPlan]);

    const planDisplayName = getPlanDisplayName();
    const isLoading = changingSubscription || monthlyPlans.length === 0;

    return (
        <div className={styles['abw-franchise-page']}>
            <div className={styles['abw-franchise-header']}>
                <div className="container">
                    <img
                        src="https://cdn.lumen5.com/lumen5-site-images/2025-website-assets/abw-mortgage/abw_x_lumen5.webp"
                        alt="A Better Way"
                        className={styles['abw-franchise-logo']}
                    />
                    <h1 className={styles['abw-franchise-title']}>
                        Lumen5 supercharges your mortgage brokerage
                    </h1>
                    <p className={styles['abw-franchise-description']}>
                        As a Mortgage Broker of A Better Way Mortgage Group, we&rsquo;re ecstatic to
                        offer you a custom Lumen5 plan. This plan, built exclusively for the needs
                        of broker agents, helps you create video content in minutes to drive your
                        business. Your custom plan includes all the features necessary to create
                        compelling videos, including AI voiceovers, translation, millions of photos
                        and videos, and more. Let&rsquo;s get creative and build your marketshare
                        today!
                    </p>
                </div>
            </div>

            <div className="container">
                {!isCouponValid && (
                    <div className="row section-3">
                        <div className="col d-flex justify-content-center align-items-center">
                            <BillingPeriodToggle
                                monthly={selectedMonthly}
                                selectMonthly={selectMonthly}
                                selectYearly={selectYearly}
                            />
                            <br />
                        </div>
                    </div>
                )}
                <div className="row justify-content-center align-items-center">
                    <div className="col-8">
                        <div className="d-flex flex-column align-items-center mt-l5-24">
                            <NotificationBlock
                                couponStatus={couponStatus}
                                upgradeStatus={upgradeStatus ?? undefined}
                                planName={planDisplayName}
                                retryUpgradeWithNewCard={retryUpgrade}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-12">
                        {currentPlan && !isLoading && (
                            <div className={`${styles['abw-plan-breakdown']} mt-l5-32 mb-l5-32`}>
                                <div className={styles['abw-plan-info']}>
                                    <h4
                                        className={`font-l5 font-l5--h4 ${styles['abw-plan-name']} mb-l5-16`}
                                    >
                                        {currentPlan.display_name}
                                    </h4>
                                    <div className={styles['abw-price-container']}>
                                        <div className={styles['abw-price']}>
                                            <span className={styles['dollar-sign']}>$</span>
                                            <span className="dollar-value">
                                                {selectedMonthly
                                                    ? currentPlan.price
                                                    : Math.ceil(currentPlan.price / 12)}
                                            </span>
                                            <span
                                                className={`font-l5 font-l5--regular ${styles['dollar-currency']}`}
                                            >
                                                {' '}
                                                CAD
                                            </span>
                                        </div>
                                        <div
                                            className={`font-l5 font-l5--subtitle-l ${styles['billing-interval']}`}
                                        >
                                            {selectedMonthly
                                                ? 'per month'
                                                : 'per month, billed yearly'}
                                        </div>
                                    </div>
                                    <div className={styles['abw-plan-button']}>
                                        <UpgradeButton
                                            plan={currentPlan}
                                            switchToPlan={switchToPlan}
                                            handlePlanSwitchError={handlePlanSwitchError}
                                            subscribeOverrideText="Get started"
                                            variant={BUTTON_VARIANTS.FILLED}
                                        />
                                    </div>
                                </div>
                                <div className={styles['abw-plan-features']}>
                                    <h6 className="font-l5 font-l5--h6">What&rsquo;s included:</h6>
                                    <p className="mb-l5-12">
                                        All the features in our{' '}
                                        <a
                                            href="/pricing"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Pro Plan
                                        </a>
                                        , priced for
                                        <br /> franchise partners, including:
                                    </p>
                                    <ul className="list-unstyled">
                                        <li className="d-flex my-l5-8">
                                            <i className="material-symbols-outlined icon-l5 text-success p-l5-4">
                                                check
                                            </i>
                                            <span>Video Translations</span>
                                        </li>
                                        <li className="d-flex my-l5-8">
                                            <i className="material-symbols-outlined icon-l5 text-success p-l5-4">
                                                check
                                            </i>
                                            <span>Custom AI Voice</span>
                                        </li>
                                        <li className="d-flex my-l5-8">
                                            <i className="material-symbols-outlined icon-l5 text-success p-l5-4">
                                                check
                                            </i>
                                            <span>Training Workshops</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )}
                        {isLoading && (
                            <div className="text-center mt-l5-32 mb-l5-32">
                                <p>Loading...</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles['abw-franchise-footer']}>
                <div className="container">
                    <p className={styles['abw-franchise-contact']}>
                        Questions? Reach out to Pius at{' '}
                        <a href="mailto:pius@lumen5.com">pius@lumen5.com</a>
                    </p>
                </div>
            </div>

            <SubscriptionChangeModal
                showing={showSubscriptionChangeConfirmation}
                subscription_data={confirmationModalData}
                coupon_data={couponStatus}
                update_subscription={updateSubscription}
                close_subscription_change_modal={closeSubscriptionChangeModal}
            />
            <PlanSwitchErrorHandlerModal
                showing={showPlanSwitchErrorModal}
                errorInfo={planSwitchErrorInfo}
                onModalClose={togglePlanSwitchErrorModal}
            />
        </div>
    );
};

export default ABWFranchisePage;
